import React, { FC, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { KeyValuePairs, LoadingSpinner } from '@/components';
import { DocumentTableData } from '@/types/depo20';
import { formatDateAndTime } from '@/utils/formatDate';

import { getDetailProforma } from '../services/getDetailProforma';
import styles from './ModalDetails.module.scss';
import { getViaje } from '../services/getViaje';
import { getDetailVerifyProforma } from '../services/getDetailVerifyProforma';
import { getProformaNumber } from '../services/getProformaNumber';

type ModalDetailsProps = {
  oid: number;
  rowData: DocumentTableData;
};

export function getDocumentType(code: string): string {
  const types: { [key: string]: string } = {
    F: 'CREDITO',
    C: 'CONTADO',
    N: 'N.CREDITO',
    D: 'DEV.CONT',
    P: 'PROFORMA',
  };

  return types[code] || 'Unknown Type';
}

export const parseProformaDetails = (
  apiResponse: string | undefined | null
) => {
  let details;
  let total;

  const removeFirstUS = (input: string): string => {
    return input.replace(/U\$S/, '').trim();
  };

  if (!apiResponse) {
    return { details: [], total: 0 };
  }
  if (apiResponse) {
    const lines = apiResponse
      .split('\r\n')
      .filter((line) => line.trim() !== '' && !line.trim().startsWith('TOTAL'));

    const groupedLines: string[] = [];
    lines.forEach((line, index) => {
      if (line.startsWith('ALMACENAJE') && lines[index + 1]) {
        groupedLines.push(
          `${line} <br /> <span class=${styles.fontWeightLight}>${removeFirstUS(lines[index + 1])}</span>`
        );
      } else if (
        !lines[index - 1] ||
        !lines[index - 1].startsWith('ALMACENAJE')
      ) {
        groupedLines.push(line);
      }
    });

    details = groupedLines.map((groupedLine: string) => {
      const [description, amountPart] = groupedLine
        .split('U$S')
        .map((part) => part.trim());
      const amountString = amountPart
        ? amountPart.split(' ')[0].replace(',', '.')
        : '0';
      const amount = parseFloat(amountString);

      return {
        key: description.replace('días a', 'días a U$S'),
        amount: Number.isNaN(amount) ? 0 : amount,
      };
    });

    const totalLinesS = apiResponse
      .split('\r\n')
      .filter((line: string) => line.trim() !== '');

    const totalLine = totalLinesS[lines.length - 1];

    const totalLines = totalLinesS.find((line: string) =>
      line.includes('TOTAL')
    );
    total = 0;
    if (totalLine) {
      const totalParts = totalLines?.split('TOTAL');
      const totalString =
        totalParts && totalParts.length > 1 ? totalParts[1].trim() : '0';
      total = totalString;
    }
  }
  return { details, total };
};

const ModalDetails: FC<ModalDetailsProps> = ({ oid, rowData }) => {
  const [isDialogShown, setIsDialogShown] = useState(false);
  const { t } = useTranslation('depo20');

  const { mutate: fetchViaje, data: viaje } = useMutation({
    mutationKey: ['ObtenerViaje', rowData?.codigoViaje],
    mutationFn: () => getViaje(rowData?.codigoViaje),
  });

  const { mutate: fetchDetailVerifyData, isPending: detailVerifyLoading } =
    useMutation({
      mutationKey: ['ObtenerDetalleVerifyProforma', oid],
      mutationFn: () => getDetailVerifyProforma(oid),
    });

  const {
    mutate: fetchDetailData,
    data: detailData,
    isPending: detailLoading,
  } = useMutation({
    mutationKey: ['ObtenerDetalle', oid],
    mutationFn: getDetailProforma,
  });

  const {
    mutate: getProformaNumberMutate,
    isPending: getProformaNumberLoading,
  } = useMutation({
    mutationKey: ['getProformaNumber', oid],
    mutationFn: () => getProformaNumber({ numero: oid }),
    onSuccess: (res) => {
      fetchDetailData(res.oid);
    },
  });

  const toggleDialog = () => {
    setIsDialogShown(!isDialogShown);
  };

  const dialogHeader = (
    <div className="text-blue-800">
      <FontAwesomeIcon icon={faEye} style={{ marginRight: '0.5rem' }} />
      {t('proformDetail')}
    </div>
  );
  const parseSerie = rowData?.serie ? ` ${rowData.serie} -` : '';
  const keyValuePairs = [
    { key: 'Fecha de Proforma', value: formatDateAndTime(rowData?.fecha) },
    {
      key: 'Fecha de liquidación',
      value: formatDateAndTime(rowData?.fechaLiquidacion ?? ''),
    },
    {
      key: 'Documento',
      value: `${getDocumentType(rowData?.tipo)} ${parseSerie} ${oid}`,
    },
    { key: 'Armador', value: rowData?.armador ?? '-' },
    {
      key: 'Buque',
      value: viaje?.buque ? `${viaje?.buque} / ${viaje?.nroViaje}` : '-',
    },
  ];

  const { details, total } = parseProformaDetails(detailData);

  return (
    <>
      <FontAwesomeIcon
        icon={faEye}
        onClick={() => {
          toggleDialog();
          fetchViaje();
          fetchDetailVerifyData();
          getProformaNumberMutate();
        }}
        style={{ cursor: 'pointer' }}
      />

      <Dialog
        header={dialogHeader}
        visible={isDialogShown}
        style={{
          maxWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
          minWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        }}
        onHide={toggleDialog}
        modal
        breakpoints={{ '1200px': '95vw' }}
      >
        <div className="pb-4 px-4">
          {(detailLoading ||
            detailVerifyLoading ||
            getProformaNumberLoading) && <LoadingSpinner />}
          {!detailLoading && (
            <>
              <KeyValuePairs pairs={keyValuePairs} boldValues />

              <ul
                className={`${styles.list} ${styles.listAddProforma} ${styles.detailProforma}`}
              >
                {rowData?.texto && (
                  <div className={styles.detailProforma}>
                    <div className={styles.text}>{rowData.texto}</div>
                  </div>
                )}
                {details?.map(
                  (result) =>
                    result && (
                      <React.Fragment key={`${result?.key}`}>
                        <li>
                          <span
                            /* eslint-disable-next-line react/no-danger */
                            dangerouslySetInnerHTML={{ __html: result?.key }}
                          />
                          <div className={styles.amount}>
                            {result?.amount?.toFixed(2)}
                          </div>
                        </li>
                        <hr />
                      </React.Fragment>
                    )
                )}
              </ul>

              {total && (
                <div className={styles.divTotalContainer}>
                  <strong>{t('totalLabel')}</strong>
                  <div className={styles.totalAmount}>
                    <h6>U$S</h6>
                    <strong>{total}</strong>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default ModalDetails;
